import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ─────────────────────────────────────────────────────────────
// Statistiques de l'Assistant IA ZIFEK (côté marchand)
// Montre la fréquentation générée par le chat IA : produits affichés aux
// clients, fiches ouvertes, ajouts au panier — avec la source bien visible.
// ─────────────────────────────────────────────────────────────

type Period = '7' | '30';

interface AiEventRow {
  id: number;
  client_user_id: number | null;
  client_name: string | null;
  event_type: string;
  product_id: number | null;
  product_name: string | null;
  query: string | null;
  created_at: string;
}

function periodStartIso(period: Period): string {
  const d = new Date();
  d.setDate(d.getDate() - parseInt(period, 10));
  return d.toISOString();
}

export default function AiChatStats() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<Period>('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<AiEventRow[]>([]);

  const fetchEvents = useCallback(async (p: Period) => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const merchantId = getCommerceId(user);
      const { data, error: err } = await supabase
        .from('ai_chat_product_events')
        .select('id, client_user_id, client_name, event_type, product_id, product_name, query, created_at')
        .eq('merchant_id', merchantId)
        .gte('created_at', periodStartIso(p))
        .order('created_at', { ascending: false })
        .limit(500);
      if (err) throw err;
      setEvents((data || []) as AiEventRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement des statistiques IA');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchEvents(period); }, [fetchEvents, period]);

  const metrics = useMemo(() => {
    const display = events.filter((e) => e.event_type === 'display').length;
    const click = events.filter((e) => e.event_type === 'click').length;
    const cart = events.filter((e) => e.event_type === 'cart').length;
    const visitors = new Set(events.filter((e) => e.client_user_id).map((e) => String(e.client_user_id))).size;
    return { display, click, cart, visitors };
  }, [events]);

  const topProducts = useMemo(() => {
    const map: Record<string, { display: number; click: number; cart: number }> = {};
    events.forEach((e) => {
      const name = e.product_name || `Produit #${e.product_id}`;
      if (!map[name]) map[name] = { display: 0, click: 0, cart: 0 };
      if (e.event_type === 'display') map[name].display += 1;
      else if (e.event_type === 'click') map[name].click += 1;
      else if (e.event_type === 'cart') map[name].cart += 1;
    });
    return Object.entries(map)
      .map(([name, counts]) => ({ name, ...counts, total: counts.display + counts.click + counts.cart }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [events]);

  const recent = useMemo(() => events.slice(0, 8), [events]);

  const EVENT_META: Record<string, { icon: string; label: string; color: string }> = {
    display: { icon: 'ri-eye-line', label: 'Affiché dans le chat', color: 'text-secondary-700 bg-secondary-100' },
    click: { icon: 'ri-cursor-line', label: 'Fiche produit ouverte', color: 'text-primary-700 bg-primary-100' },
    cart: { icon: 'ri-shopping-cart-2-line', label: 'Ajouté au panier', color: 'text-accent-700 bg-accent-100' },
  };

  return (
    <div className="bg-background-50 border border-accent-200/60 rounded-lg p-5 mb-6">
      {/* En-tête avec badge de source bien visible */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-robot-2-line"></i>
            </span>
            Activité de l&apos;Assistant IA ZIFEK
          </h3>
          <p className="text-xs text-foreground-400 mt-1 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium">
              <i className="ri-robot-2-line text-[10px]"></i>
              Source : chat IA
            </span>
            Produits vus par les clients via l&apos;assistant, dans leur espace personnel.
          </p>
        </div>
        <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 self-start">
          {(['7', '30'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                period === p ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {p} jours
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex flex-col items-center justify-center py-5 mb-4">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={() => fetchEvents(period)}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Métriques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-background-100 rounded-lg p-3.5">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-2">
            <i className="ri-eye-line"></i>
          </div>
          <div className="text-xl font-bold font-heading text-foreground-950">{metrics.display}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Produits affichés</div>
        </div>
        <div className="bg-background-100 rounded-lg p-3.5">
          <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-2">
            <i className="ri-cursor-line"></i>
          </div>
          <div className="text-xl font-bold font-heading text-foreground-950">{metrics.click}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Fiches ouvertes</div>
        </div>
        <div className="bg-background-100 rounded-lg p-3.5">
          <div className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-2">
            <i className="ri-shopping-cart-2-line"></i>
          </div>
          <div className="text-xl font-bold font-heading text-foreground-950">{metrics.cart}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Ajouts au panier</div>
        </div>
        <div className="bg-background-100 rounded-lg p-3.5">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-2">
            <i className="ri-user-search-line"></i>
          </div>
          <div className="text-xl font-bold font-heading text-foreground-950">{metrics.visitors}</div>
          <div className="text-[11px] text-foreground-500 mt-0.5">Clients uniques</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-foreground-400">
          <i className="ri-robot-2-line text-3xl mb-2"></i>
          <p className="text-sm">Aucune interaction via l&apos;assistant IA sur cette période</p>
          <p className="text-xs mt-0.5">Les recherches de vos produits dans le chat IA apparaîtront ici</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Top produits vus via l'IA */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 mb-3 flex items-center gap-1.5">
              <i className="ri-trophy-line text-accent-500"></i>
              Produits les plus consultés via l&apos;IA
            </h4>
            <div className="space-y-2.5">
              {topProducts.map((p, i) => {
                const max = topProducts[0]?.total || 1;
                return (
                  <div key={p.name} className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center text-[10px] font-bold text-accent-700 flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground-900 truncate">{p.name}</div>
                      <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-accent-400 rounded-full transition-all" style={{ width: `${(p.total / max) * 100}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] whitespace-nowrap">
                      <span className="text-secondary-700">{p.display}</span>
                      <i className="ri-cursor-line text-primary-500"></i>
                      <span className="text-accent-700">{p.cart}</span>
                      <i className="ri-shopping-cart-2-line text-accent-500"></i>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Activité récente */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-600 mb-3 flex items-center gap-1.5">
              <i className="ri-history-line text-primary-500"></i>
              Dernières interactions IA
            </h4>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {recent.map((e) => {
                const meta = EVENT_META[e.event_type] || EVENT_META.display;
                return (
                  <div key={e.id} className="flex items-start gap-2.5 py-2 border-b border-background-200/60 last:border-0">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <i className={`${meta.icon} text-xs`}></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-foreground-800 truncate">
                        <span className="font-medium">{e.client_name || 'Un client'}</span>{' '}
                        <span className="text-foreground-500">· {meta.label}</span>
                      </p>
                      <p className="text-[11px] text-foreground-500 truncate">{e.product_name || `Produit #${e.product_id}`}</p>
                      {e.query && (
                        <p className="text-[10px] text-foreground-400 truncate italic">« {e.query} »</p>
                      )}
                    </div>
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}